import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { VetService } from '../services/vetService';
import { 
    validateAppointmentForm, 
    transformAppointmentForSubmission,
    sortAppointmentsByDate,
    filterAppointmentsByStatus,
    searchAppointments,
    logDuplicateAppointments
} from '../utils/vetUtils';

const useVetAppointments = () => {
    const { authorizedFetch } = useAuth();
    // State management
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        searchTerm: ''
    });

    // Get filtered appointments
    const getFilteredAppointments = useCallback(() => {
        let filtered = [...appointments];

        // Apply status filter
        if (filters.status) {
            filtered = filterAppointmentsByStatus(filtered, filters.status);
        }

        // Apply search filter
        if (filters.searchTerm) {
            filtered = searchAppointments(filtered, filters.searchTerm);
        }

        // Sort by date
        return sortAppointmentsByDate(filtered);
    }, [appointments, filters]);

    // Load all appointments
    const loadAppointments = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const data = await VetService.getAllAppointments(authorizedFetch);
            setAppointments(data);
            
            // Check for duplicates and log them
            logDuplicateAppointments(data);
        } catch (error) {
            console.error('Error loading appointments:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [authorizedFetch]);

    // Load appointments for a specific dog
    const loadAppointmentsByDog = useCallback(async (dogId) => {
        setLoading(true);
        setError(null);
        
        try {
            const data = await VetService.getAppointmentsByDog(dogId, authorizedFetch);
            setAppointments(data);
        } catch (error) {
            console.error('Error loading dog appointments:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [authorizedFetch]);

    // Create new appointment
    const createAppointment = useCallback(async (appointmentData) => {
        // Validate form data
        const validation = validateAppointmentForm(appointmentData);
        if (!validation.isValid) {
            throw new Error('Validation failed: ' + Object.values(validation.errors).join(', '));
        }

        setError(null);
        
        try {
            // Transform data for API
            const transformedData = transformAppointmentForSubmission(appointmentData);
            
            // Create appointment
            const newAppointment = await VetService.createAppointment(transformedData, authorizedFetch);
            
            // Update local state
            setAppointments(prev => [newAppointment, ...prev]);
            
            return newAppointment;
        } catch (error) {
            console.error('Error creating appointment:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Update existing appointment
    const updateAppointment = useCallback(async (appointmentId, updateData, isExamUpdate = false) => {
        // Validate update data - skip basic validation for exam updates
        const validation = validateAppointmentForm(updateData, isExamUpdate);
        if (!validation.isValid) {
            throw new Error('Validation failed: ' + Object.values(validation.errors).join(', '));
        }

        setError(null);
        
        try {
            // Transform data for API
            const transformedData = transformAppointmentForSubmission(updateData, isExamUpdate);
            
            // Update appointment
            const updatedAppointment = await VetService.updateAppointment(appointmentId, transformedData, authorizedFetch);
            
            // Update local state
            setAppointments(prev => 
                prev.map(appointment => 
                    appointment._id === appointmentId ? updatedAppointment : appointment
                )
            );
            
            return updatedAppointment;
        } catch (error) {
            console.error('Error updating appointment:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Create puppy vaccination schema (5 appointments at once)
    const createPuppySchema = useCallback(async (data) => {
        setError(null);
        try {
            const newAppointments = await VetService.createPuppySchema(data, authorizedFetch);
            setAppointments(prev => [...newAppointments, ...prev]);
            return newAppointments;
        } catch (error) {
            console.error('Error creating puppy schema:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Create adult vaccination schema (today + 1-year follow-up)
    const createAdultSchema = useCallback(async (data) => {
        setError(null);
        try {
            const newAppointments = await VetService.createAdultSchema(data, authorizedFetch);
            setAppointments(prev => [...newAppointments, ...prev]);
            return newAppointments;
        } catch (error) {
            console.error('Error creating adult schema:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Create a walk-in appointment (bypasses scheduled-booking validation)
    const createWalkInAppointment = useCallback(async (data) => {
        setError(null);
        try {
            const newAppointment = await VetService.createAppointment(data, authorizedFetch);
            setAppointments(prev => [newAppointment, ...prev]);
            return newAppointment;
        } catch (error) {
            console.error('Error creating walk-in appointment:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Update a walk-in appointment
    const updateWalkInAppointment = useCallback(async (appointmentId, data) => {
        setError(null);
        try {
            const updated = await VetService.updateAppointment(appointmentId, data, authorizedFetch);
            setAppointments(prev => prev.map(a => a._id === appointmentId ? updated : a));
            return updated;
        } catch (error) {
            console.error('Error updating walk-in appointment:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Update appointment status only
    const updateAppointmentStatus = useCallback(async (appointmentId, newStatus) => {
        setError(null);
        
        try {
            const updatedAppointment = await VetService.updateAppointment(appointmentId, { status: newStatus }, authorizedFetch);
            
            // Update local state
            setAppointments(prev => 
                prev.map(appointment => 
                    appointment._id === appointmentId ? updatedAppointment : appointment
                )
            );
            
            return updatedAppointment;
        } catch (error) {
            console.error('Error updating appointment status:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Delete appointment
    const deleteAppointment = useCallback(async (appointmentId) => {
        setError(null);
        
        try {
            await VetService.deleteAppointment(appointmentId, authorizedFetch);
            
            // Update local state
            setAppointments(prev => prev.filter(appointment => appointment._id !== appointmentId));
            
            return true;
        } catch (error) {
            console.error('Error deleting appointment:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Get appointment by ID
    const getAppointmentById = useCallback(async (appointmentId) => {
        setError(null);
        
        try {
            const appointment = await VetService.getAppointmentById(appointmentId, authorizedFetch);
            return appointment;
        } catch (error) {
            console.error('Error fetching appointment:', error);
            setError(error.message);
            throw error;
        }
    }, [authorizedFetch]);

    // Update filters
    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    // Clear filters
    const clearFilters = useCallback(() => {
        setFilters({
            status: '',
            searchTerm: ''
        });
    }, []);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Load appointments on mount
    useEffect(() => {
        loadAppointments();
    }, [loadAppointments]);

    // Return hook interface
    return {
        // Data
        appointments: getFilteredAppointments(),
        allAppointments: appointments,
        loading,
        error,
        filters,

        // Actions
        loadAppointments,
        loadAppointmentsByDog,
        createAppointment,
        createPuppySchema,
        createAdultSchema,
        createWalkInAppointment,
        updateAppointment,
        updateWalkInAppointment,
        updateAppointmentStatus,
        deleteAppointment,
        getAppointmentById,

        // Filters
        updateFilters,
        clearFilters,

        // Utils
        clearError
    };
};

export default useVetAppointments;